import React from "react";
import PageForm from "../../templates/PageForm";
import { useNavigate } from "react-router-dom";
import TextEdit from "../../molecules/TextEdit";
import SelectTextField from "../../molecules/SelectTextField";
import MultiEmailInput from "./MultiEmailInput";
import AttachmentInput from "./AttachmentInput";
import TagInput from "./TagInput";
import TemplateVariableList from "./TemplateVariableList";
import TemplateVariableModalForm from "./TemplateVariableModalForm";
import { useSignal } from "@preact/signals-react";
import { toast } from "react-toastify";
import { ErrorField } from '../../../types/ErrorField';
import { getErrorMessage, removeErrorField } from '../../../utils/formErrorUtils';
import CodeMirror from '@uiw/react-codemirror';
import { EditorView } from '@codemirror/view';
import { html } from '@codemirror/lang-html';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { HiOutlineMail } from 'react-icons/hi';
import { FaEdit } from 'react-icons/fa';
import { getFormatIcon } from './formatUtils';

type TemplateVariable = {
    name: string;
    display: string;
    field: string;
    format: string;
    type?: string;
    size?: number;
    properties?: Array<{
        name: string;
        display?: string;
        format: string;
        size?: number;
    }>;
    error?: string;
}

type AttachmentConfig = {
    filename: string;
    type: 'link' | 'file';
    expirationMinutes?: number;
};

type Tag = {
    name: string;
    value: string;
};

type Form = {
    id: string;
    type: string;
    description: string;
    providerTemplateName: string;
    mailFrom: string[];
    mailTo: string[];
    mailSubject: string;
    mailBody: string;
    mailCc: string[];
    mailBcc: string[];
    mailReplyTo: string;
    configurationSet: string;
    attachments: AttachmentConfig[];
    tags: Tag[];
    variables: TemplateVariable[];
}

const TemplateMailPageForm: React.FC = () => {
    const navigate = useNavigate();
    const isLoadingForm = useSignal<boolean>(false);
    const errorFields = useSignal<ErrorField[]>([]);
    const apiVersion = 'Communication API - v1.0.0';
    const showVariableModal = useSignal<boolean>(false);
    const editingVariableIndex = useSignal<number>(-1);
    const showHtmlEditor = useSignal<boolean>(false);
    const newVariable = useSignal<TemplateVariable>({
        name: "",
        display: "",
        field: "",
        format: "TEXT",
        size: 100,
    });

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'font': [] }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'script': 'sub'}, { 'script': 'super' }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            [{ 'align': [] }],
            ['blockquote', 'code-block'],
            ['link', 'image', 'video'],
            ['clean']
        ],
    };

    const CONFIGURATION_SETS = [
        { value: "", label: "Nenhum" },
        { value: "MAIL_DUPLICATE_BILLING", label: "MAIL_DUPLICATE_BILLING" },
        { value: "MAIL_STRATEGY_FUNDRAISING", label: "MAIL_STRATEGY_FUNDRAISING" }
    ];

    const EMAIL_BUCKETS = [
        "Recibos",
        "Boleto",
        "Boleto imagem",
        "Contratos"
    ];

    const form = useSignal<Form>({
        id: "",
        type: "MAIL",
        description: "",
        providerTemplateName: "",
        mailFrom: [],
        mailTo: [],
        mailSubject: "",
        mailBody: "",
        mailCc: [],
        mailBcc: [],
        mailReplyTo: "",
        configurationSet: "",
        attachments: [],
        tags: [],
        variables: [],
    });

    const toDisplayFormat = (name: string): string => {
        return name
            .replace(/[_-]/g, ' ')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .toLowerCase()
            .trim()
            .replace(/^\w/, c => c.toUpperCase());
    };

    const toCamelCase = (name: string): string => {
        const lower = name.toLowerCase();
        return lower.replace(/[_-](\w)/g, (_, c) => c.toUpperCase());
    };

    const detectVariablesFromText = (text: string) => {
        const existingVars = form.value.variables;
        const detectedVars = new Map<string, TemplateVariable>();

        const simpleMatches = text.match(/\{\{(\w+)\}\}/g);
        if (simpleMatches) {
            simpleMatches.forEach((match: string) => {
                const varName = match.match(/\{\{(\w+)\}\}/)?.[1];
                if (varName && !detectedVars.has(varName)) {
                    const existing = existingVars.find(v => v.name === varName);
                    
                    // Check if variable comes from email fields
                    const emailFieldsText = [
                        ...form.value.mailFrom,
                        ...form.value.mailTo,
                        ...form.value.mailCc,
                        ...form.value.mailBcc,
                        form.value.mailReplyTo
                    ].join(' ');
                    const isEmailVar = emailFieldsText.includes(`{{${varName}}}`);
                    const isAttachmentVar = form.value.attachments.some(a => a.filename.includes(`{{${varName}}}`));                    
                    detectedVars.set(varName, {
                        name: varName,
                        display: existing?.display || toDisplayFormat(varName),
                        field: existing?.field || toCamelCase(varName),
                        format: isEmailVar ? 'email' : (isAttachmentVar ? 'fileUrl' : (existing?.format || 'text')),
                        type: isEmailVar ? 'email' : 'text',
                        size: existing?.size || 50,
                    });
                }
            });
        }

        form.value = {
            ...form.value,
            variables: Array.from(detectedVars.values())
        };
    };

    const updateVariable = (index: number, updates: Partial<TemplateVariable>) => {
        const newVars = [...form.value.variables];
        newVars[index] = { ...newVars[index], ...updates };
        form.value = { ...form.value, variables: newVars };
        editingVariableIndex.value = -1;
        newVariable.value = { name: "", display: "", field: "", format: "TEXT", size: 100 };
        showVariableModal.value = false;
    };

    const submitForm = async () => {
        isLoadingForm.value = true;
        try {
            toast.info("Template de email criado com sucesso!");
            navigate(-1);
        } catch (error) {
            toast.error("Ocorreu um erro. Tente novamente mais tarde.");
        }
        isLoadingForm.value = false;
    };

    const changeForm = (field: string, value: string | string[] | AttachmentConfig[] | Tag[]) => {
        form.value = {
            ...form.value,
            [field]: value,
        };
        removeErrorField(errorFields.value, field);

        if (['mailBody', 'mailSubject', 'mailReplyTo', 'mailFrom', 'mailTo', 'mailCc', 'mailBcc', 'attachments', 'tags'].includes(field)) {
            const allText = [
                form.value.mailBody,
                form.value.mailSubject,
                form.value.mailReplyTo,
                ...form.value.mailFrom,
                ...form.value.mailTo,
                ...form.value.mailCc,
                ...form.value.mailBcc,
                ...form.value.attachments.map(a => a.filename),
                ...form.value.tags.map(t => t.value)
            ].join(' ');
            detectVariablesFromText(allText);
        }
    };

    const cancelForm = () => {
        navigate(-1);
    };

    return (
        <PageForm
            pageTitle="Novo template de email"
            apiVersion={apiVersion}
            isLoading={isLoadingForm.value}
            breadcrumb={[
                { label: "Templates de email", url: "/communication/templates/mail/0/20/id/ASC" },
                { label: "Novo template de email", url: "#" },
            ]}
            controlSlot={(
                <div className="flex space-x-4 justify-end m-2">
                    <button
                        className='text-black bg-slate-200 px-4 py-2 hover:bg-slate-400 rounded dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600'
                        onClick={cancelForm}
                        disabled={isLoadingForm.value}
                    >
                        Cancelar
                    </button>
                    <button
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-800 disabled:opacity-50 dark:bg-blue-600 dark:hover:bg-blue-700"
                        onClick={submitForm}
                        disabled={isLoadingForm.value}
                    >
                        Salvar
                    </button>
                </div>
            )}
        >
            <div className="flex">
                <div className="flex-1 container mx-auto p-4">
                    <div className="grid grid-cols-12 gap-4 mb-4">
                        <div className="col-span-6">
                            <TextEdit
                                label="Nome (único)"
                                value={form.value.id}
                                onChange={(e) => changeForm("id", e.target.value)}
                                errorMessage={getErrorMessage(errorFields.value, "id")}
                                placeholder=""
                                maxLength={100}
                            />
                        </div>
                        <div className="col-span-6">
                            <TextEdit
                                label="Descrição detalhada"
                                value={form.value.description}
                                onChange={(e) => changeForm("description", e.target.value)}
                                errorMessage={getErrorMessage(errorFields.value, "description")}
                                placeholder=""
                                maxLength={200}
                            />
                        </div>
                    </div>

                    <div className="border border-gray-300 rounded-lg p-4 mb-4 dark:border-slate-600">
                        <h3 className="text-lg font-semibold mb-4 dark:text-white flex items-center gap-2">
                            <HiOutlineMail className="text-blue-600 dark:text-blue-400" />
                            Envelope do Email
                        </h3>
                        
                        <div className="grid grid-cols-12 gap-4 mb-4">
                            <div className="col-span-6">
                                <MultiEmailInput
                                    label="De (From)"
                                    value={form.value.mailFrom}
                                    onChange={(values) => changeForm("mailFrom", values)}
                                    errorMessage={getErrorMessage(errorFields.value, "mailFrom")}
                                    helperText="Use Tab, vírgula ou ; para separar. {{variavel}} detectado automaticamente"
                                />
                            </div>
                            <div className="col-span-6">
                                <MultiEmailInput
                                    label="Para (To)"
                                    value={form.value.mailTo}
                                    onChange={(values) => changeForm("mailTo", values)}
                                    errorMessage={getErrorMessage(errorFields.value, "mailTo")}
                                    helperText="Use Tab, vírgula ou ; para separar. {{variavel}} detectado automaticamente"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-12 gap-4 mb-4">
                            <div className="col-span-4">
                                <MultiEmailInput
                                    label="Cópia (CC)"
                                    value={form.value.mailCc}
                                    onChange={(values) => changeForm("mailCc", values)}
                                    errorMessage={getErrorMessage(errorFields.value, "mailCc")}
                                />
                            </div>
                            <div className="col-span-4">
                                <MultiEmailInput
                                    label="Cópia Oculta (BCC)"
                                    value={form.value.mailBcc}
                                    onChange={(values) => changeForm("mailBcc", values)}
                                    errorMessage={getErrorMessage(errorFields.value, "mailBcc")}
                                />
                            </div>
                            <div className="col-span-4">
                                <TextEdit
                                    label="Responder Para (Reply-To)"
                                    value={form.value.mailReplyTo}
                                    onChange={(e) => changeForm("mailReplyTo", e.target.value)}
                                    errorMessage={getErrorMessage(errorFields.value, "mailReplyTo")}
                                    placeholder=""
                                    maxLength={200}
                                    helperText="Apenas um email ou variável"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="border border-gray-300 rounded-lg p-4 mb-4 dark:border-slate-600">
                        <h3 className="text-lg font-semibold mb-4 dark:text-white">Conteúdo do Email</h3>
                        
                        <div className="grid grid-cols-12 gap-4 mb-4">
                            <div className="col-span-6">
                                <TextEdit
                                    label="Assunto"
                                    value={form.value.mailSubject}
                                    onChange={(e) => changeForm("mailSubject", e.target.value)}
                                    errorMessage={getErrorMessage(errorFields.value, "mailSubject")}
                                    placeholder=""
                                    maxLength={200}
                                    helperText="Use {{variavel}} para valores dinâmicos"
                                />
                            </div>
                            <div className="col-span-6">
                                <AttachmentInput
                                    label="Anexos (Attachments)"
                                    value={form.value.attachments}
                                    onChange={(values) => changeForm("attachments", values)}
                                    errorMessage={getErrorMessage(errorFields.value, "attachments")}
                                />
                            </div>
                        </div>

                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => showHtmlEditor.value = !showHtmlEditor.value}
                                className="absolute top-2 right-2 z-10 p-2 rounded bg-white hover:bg-gray-100 border border-gray-300 shadow-sm dark:bg-slate-700 dark:hover:bg-slate-600 dark:border-slate-600"
                                title={showHtmlEditor.value ? 'Editor Visual' : 'Código HTML'}
                            >
                                {showHtmlEditor.value ? '📝' : '💻'}
                            </button>
                            <div className="border border-gray-300 rounded-lg dark:border-slate-600 overflow-hidden">
                                {showHtmlEditor.value ? (
                                    <CodeMirror
                                        value={form.value.mailBody}
                                        onChange={(value) => changeForm('mailBody', value)}
                                        height="500px"
                                        theme="light"
                                        extensions={[
                                            EditorView.lineWrapping,
                                            html()
                                        ]}
                                        basicSetup={{
                                            lineNumbers: true,
                                            highlightActiveLineGutter: true,
                                            highlightActiveLine: true,
                                            foldGutter: true,
                                        }}
                                    />
                                ) : (
                                    <ReactQuill
                                        theme="snow"
                                        value={form.value.mailBody}
                                        onChange={(value) => changeForm('mailBody', value)}
                                        modules={quillModules}
                                        style={{ height: '500px', marginBottom: '42px' }}
                                    />
                                )}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Use {`{{variavel}}`} para valores dinâmicos dentro do HTML
                            </p>
                            {getErrorMessage(errorFields.value, 'mailBody') && (
                                <p className="text-red-500 text-xs mt-1 dark:text-red-400">
                                    {getErrorMessage(errorFields.value, 'mailBody')}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="border border-gray-300 rounded-lg p-4 mb-4 dark:border-slate-600">
                        <h3 className="text-lg font-semibold mb-4 dark:text-white">Configuração do SES</h3>
                        
                        <div className="grid grid-cols-12 gap-4 mb-4">
                            <div className="col-span-6">
                                <TextEdit
                                    label="Nome no provedor (AWS SES)"
                                    value={form.value.providerTemplateName}
                                    onChange={(e) => changeForm("providerTemplateName", e.target.value)}
                                    errorMessage={getErrorMessage(errorFields.value, "providerTemplateName")}
                                    placeholder=""
                                    maxLength={100}
                                />
                            </div>
                            <div className="col-span-6">
                                <SelectTextField
                                    label="Configuration Set"
                                    value={form.value.configurationSet}
                                    onChange={(e) => changeForm("configurationSet", e.target.value)}
                                    items={CONFIGURATION_SETS}
                                    emptyValue=""
                                    emptyLabel="Nenhum"
                                    errorMessage={getErrorMessage(errorFields.value, "configurationSet")}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-12">
                                <TagInput
                                    label="Tags"
                                    value={form.value.tags}
                                    onChange={(values) => changeForm("tags", values)}
                                    errorMessage={getErrorMessage(errorFields.value, "tags")}
                                    variables={form.value.variables}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="w-80 border-l border-gray-300 dark:border-slate-600 p-4 overflow-y-auto max-h-screen sticky top-0">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold dark:text-white">Variáveis Detectadas</h3>
                    </div>

                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 dark:bg-blue-900 dark:border-blue-700 dark:text-blue-300">
                        <p className="font-semibold mb-1">💡 Tipos de uso:</p>
                        <ul className="list-disc list-inside space-y-1">
                            <li><strong>String:</strong> {`{{nome}}`} dentro do texto</li>
                            <li><strong>Lista:</strong> {`{{emails}}`} como item único</li>
                            <li><strong>Múltiplos:</strong> email1, {`{{var}}`}, email2</li>
                        </ul>
                    </div>

                    <TemplateVariableList
                        variables={form.value.variables}
                        onEdit={(variable, index) => {
                            newVariable.value = { ...variable };
                            editingVariableIndex.value = index;
                            showVariableModal.value = true;
                        }}
                    />
                </div>
            </div>

            <TemplateVariableModalForm
                show={showVariableModal}
                variable={newVariable}
                onAdd={updateVariable}
                existingVariables={form.value.variables}
                editingIndex={editingVariableIndex.value}
            />
        </PageForm>
    );
};

export default TemplateMailPageForm;
